<template>
  <div class="guide-sheet">

    <div v-if="loading" class="guide-sheet__empty">불러오는 중...</div>

    <div v-else-if="!info" class="guide-sheet__empty">
      좌석표를 찾을 수 없습니다. 링크를 다시 확인해 주십시오.
    </div>

    <template v-else>
      <div class="guide-sheet__actions">
        <button type="button" class="btn" @click="reload">새로고침</button>
        <button type="button" class="btn btn--primary" @click="printSheet">인쇄 / PDF 저장</button>
      </div>

      <!-- 헤더 -->
      <header class="guide-sheet__head">
        <div class="guide-sheet__title">
          <h1>{{ info.routeName }}</h1>
          <span class="guide-sheet__bus">{{ info.busSeq }}호차</span>
        </div>
        <dl class="guide-sheet__meta">
          <div><dt>출발일</dt><dd>{{ fmtDate(info.roundDate) }}</dd></div>
          <div><dt>출발시각</dt><dd>{{ info.departTime || '-' }}</dd></div>
          <div><dt>집결지</dt><dd>{{ info.meetingPointName || '-' }}<template v-if="info.meetingAddress"> ({{ info.meetingAddress }})</template></dd></div>
          <div><dt>탑승 인원</dt><dd>{{ totalPax }}명 (좌석 지정 {{ info.seatedCnt }}석)</dd></div>
        </dl>
        <pre v-if="info.guideInfo" class="guide-sheet__guide-info">{{ info.guideInfo }}</pre>
        <p class="guide-sheet__stamp">출력 기준 {{ printedAt }}</p>
      </header>

      <!-- 좌석표 (인쇄 1페이지) -->
      <section class="guide-sheet__section guide-sheet__section--map">
        <h2>좌석표</h2>
        <div class="guide-sheet__bus-map">
          <div class="guide-sheet__driver">운전석</div>
          <div v-for="(row, rIdx) in seatRows" :key="rIdx" class="guide-sheet__row">
            <span class="guide-sheet__row-no">{{ rIdx + 1 }}</span>
            <template v-for="seat in row" :key="seat.seatNo">
              <div class="guide-sheet__seat" :class="seatClass(seat)">
                <span class="guide-sheet__seat-no">{{ seat.seatNo }}</span>
                <span class="guide-sheet__seat-name">{{ seat.passName || seat.reqName || (seat.seatGrade === 'blocked' ? '×' : '') }}</span>
              </div>
              <span v-if="seat.aisleAfter === 'Y'" class="guide-sheet__aisle" />
            </template>
          </div>
        </div>
      </section>

      <!-- 탑승자 명단 (인쇄 2페이지부터) -->
      <section class="guide-sheet__section guide-sheet__section--list">
        <p class="guide-sheet__print-title">{{ info.routeName }} {{ info.busSeq }}호차 · {{ fmtDate(info.roundDate) }}{{ info.departTime ? ' ' + info.departTime : '' }}</p>
        <h2>탑승자 명단 <small>({{ passengers.length }}팀 · {{ totalPax }}명)</small></h2>
        <p v-if="!info.manifestAt" class="guide-sheet__note">아직 명단이 등록되지 않아 홈페이지 예약만 표시됩니다.</p>
        <table class="guide-sheet__table">
          <thead>
            <tr>
              <th>No</th>
              <th>좌석</th>
              <th>탑승자</th>
              <th>인원</th>
              <th>연락처</th>
              <th>비고</th>
              <th>구매채널</th>
              <th class="guide-sheet__check">탑승</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(p, i) in sortedPassengers" :key="p.grp + '-' + p.rowNo + '-' + i"
                :class="{ 'is-extra': Number(p.grp) === 2, 'is-seated': p.seatNos, 'is-seated-last': i === seatedCnt - 1 }">
              <td>{{ i + 1 }}</td>
              <td>{{ p.seatNos || '-' }}</td>
              <td>
                {{ p.paxName }}
                <div v-if="p.passName" class="guide-sheet__pass">{{ p.passName }}</div>
              </td>
              <td>{{ p.paxCnt }}</td>
              <td>{{ p.telNo }}</td>
              <td>{{ p.memo }}</td>
              <td>{{ p.channel }}</td>
              <td class="guide-sheet__check"><span class="guide-sheet__box" /></td>
            </tr>
            <tr v-if="passengers.length === 0">
              <td colspan="8" class="guide-sheet__empty">탑승자가 없습니다.</td>
            </tr>
          </tbody>
        </table>
      </section>
    </template>
  </div>
</template>

<script>
// 가이드용 호차 좌석표 - 로그인 없이 링크(/busGuide?t=토큰)로 열리는 페이지
// BusGuideSheet-2: 탑승자 명단에서 좌석이 지정된 예약을 위에 좌석 순으로 표시
// BusGuideSheet-4: 인쇄 시 1페이지 = 헤더 + 좌석표, 2페이지부터 = 탑승자 명단
// 휴대폰에서 바로 보고, [인쇄 / PDF 저장]으로 브라우저 인쇄창에서 PDF로 저장할 수 있어요.
export default {
  name: 'BusGuideSheet',

  data() {
    return {
      token: '',
      loading: true,
      info: null,
      seats: [],
      passengers: [],
      printedAt: '',
    };
  },

  computed: {
    seatRows() {
      const rows = [];
      this.seats.forEach((s) => {
        const rIdx = s.seatRow - 1;
        if (!rows[rIdx]) rows[rIdx] = [];
        rows[rIdx][s.seatOrder - 1] = s;
      });
      return rows.filter(Boolean).map((r) => r.filter(Boolean));
    },
    totalPax() {
      return this.passengers.reduce((sum, p) => sum + (Number(p.paxCnt) || 0), 0);
    },
    // 좌석번호 → 좌석표 순서 (앞줄부터, 줄 안에서는 왼쪽부터)
    seatOrderMap() {
      const map = {};
      this.seats.forEach((s, idx) => { map[s.seatNo] = idx; });
      return map;
    },
    // 명단 정렬: 좌석이 지정된 예약(멤버 좌석지정·관리자 배정)을 위에 좌석 순으로,
    //           나머지는 받은 순서(엑셀 명단 순 → 명단 외) 그대로 아래에
    sortedPassengers() {
      const firstSeat = (p) => {
        const nos = String(p.seatNos || '').split(',').map((v) => v.trim()).filter(Boolean);
        const idxs = nos.map((no) => this.seatOrderMap[no]).filter((v) => v !== undefined);
        return idxs.length ? Math.min(...idxs) : Number.MAX_SAFE_INTEGER;
      };
      const seated = this.passengers.filter((p) => p.seatNos).sort((a, b) => firstSeat(a) - firstSeat(b));
      const others = this.passengers.filter((p) => !p.seatNos);
      return [...seated, ...others];
    },
    seatedCnt() {
      return this.passengers.filter((p) => p.seatNos).length;
    },
  },

  created() {
    this.token = String(this.$route.query.t || '');
    this.reload();
  },

  mounted() {
    // 사이트 공통 상·하단 메뉴는 좌석표에서 숨김
    ['admin', 'user'].forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.style.display = 'none';
    });
  },

  methods: {
    ymd(d) {
      const p = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
    },
    fmtDate(v) {
      if (!v) return '-';
      const s = String(v);
      const d = s.includes('T') && s.endsWith('Z') ? new Date(s) : new Date(s.replace(' ', 'T'));
      if (isNaN(d)) return s.slice(0, 10);
      const week = ['일', '월', '화', '수', '목', '금', '토'][d.getDay()];
      return `${this.ymd(d)} (${week})`;
    },

    async reload() {
      this.loading = true;
      try {
        if (!this.token) {
          this.info = null;
          return;
        }
        const [infoRes, seatRes, paxRes] = await Promise.all([
          this.$api('/api/getBusGuideInfo', { param: [this.token] }),
          this.$api('/api/getBusGuideSeats', { param: [this.token] }),
          this.$api('/api/getBusGuidePassengers', { param: [this.token, this.token] }),
        ]);
        this.info = (Array.isArray(infoRes) && infoRes[0]) || null;
        this.seats = Array.isArray(seatRes) ? seatRes : [];
        this.passengers = Array.isArray(paxRes) ? paxRes : [];

        const now = new Date();
        const p = (n) => String(n).padStart(2, '0');
        this.printedAt = `${this.ymd(now)} ${p(now.getHours())}:${p(now.getMinutes())}`;
        if (this.info) document.title = `${this.info.routeName} ${this.info.busSeq}호차 좌석표`;
      } finally {
        this.loading = false;
      }
    },

    seatClass(seat) {
      if (seat.reqName) return 'is-taken';
      if (seat.seatGrade === 'blocked') return 'is-blocked';
      return '';
    },

    printSheet() {
      window.print();
    },
  },
};
</script>

<style scoped>
.guide-sheet {
  max-width: 820px;
  margin: 0 auto;
  padding: 20px 16px 40px;
  color: #222;
  font-size: 13px;
}
.guide-sheet__actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  margin-bottom: 12px;
}
.guide-sheet__empty {
  text-align: center;
  color: #999;
  padding: 40px 0;
}

.guide-sheet__head {
  border-bottom: 2px solid #222;
  padding-bottom: 12px;
  margin-bottom: 16px;
}
.guide-sheet__title {
  display: flex;
  align-items: baseline;
  gap: 10px;
}
.guide-sheet__title h1 {
  font-size: 20px;
  font-weight: 700;
  margin: 0;
}
.guide-sheet__bus {
  font-size: 18px;
  font-weight: 700;
  color: #2f7d5f;
}
.guide-sheet__meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px 20px;
  margin: 10px 0 0;
}
.guide-sheet__meta div {
  display: flex;
  gap: 8px;
}
.guide-sheet__meta dt {
  width: 64px;
  flex: none;
  color: #777;
}
.guide-sheet__meta dd {
  margin: 0;
  font-weight: 600;
}
.guide-sheet__guide-info {
  margin: 10px 0 0;
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fafafa;
  font-family: inherit;
  font-size: 12px;
  white-space: pre-wrap;
}
.guide-sheet__note {
  margin: -4px 0 8px;
  font-size: 12px;
  color: #b5541f;
}
.guide-sheet__table tr.is-extra td {
  background: #fff8ec;
}
.guide-sheet__table tr.is-seated td:nth-child(2) {
  font-weight: 700;
}
/* 좌석 지정 예약과 나머지 사이 구분선 */
.guide-sheet__table tr.is-seated-last td {
  border-bottom: 2px solid #555;
}
.guide-sheet__stamp {
  margin: 8px 0 0;
  font-size: 11px;
  color: #999;
}

.guide-sheet__section {
  margin-bottom: 20px;
}
.guide-sheet__section h2 {
  font-size: 15px;
  font-weight: 700;
  margin: 0 0 10px;
}
.guide-sheet__section h2 small {
  font-size: 12px;
  font-weight: 400;
  color: #777;
}

/* 좌석표 */
.guide-sheet__bus-map {
  display: inline-block;
  border: 1px solid #ccc;
  border-radius: 12px;
  padding: 12px;
  max-width: 100%;
  overflow-x: auto;
  box-sizing: border-box;
}
.guide-sheet__driver {
  width: 48px;
  margin: 0 0 10px auto;
  padding: 2px 0;
  border: 1px solid #ccc;
  border-radius: 6px;
  text-align: center;
  font-size: 10px;
  color: #999;
}
.guide-sheet__row {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 5px;
}
.guide-sheet__row-no {
  width: 16px;
  font-size: 10px;
  color: #999;
}
.guide-sheet__seat {
  flex: 0 0 auto;
  width: 64px;
  height: 48px;
  padding: 2px 3px;
  box-sizing: border-box;
  border: 1px solid #bbb;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1.15;
  background: #fff;
}
.guide-sheet__seat-no {
  font-size: 10px;
  color: #888;
}
/* 여권명 우선 - 2줄까지 표시 */
.guide-sheet__seat-name {
  width: 100%;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.15;
  text-align: center;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.guide-sheet__seat.is-taken {
  background: #e1f5ee;
  border-color: #2f7d5f;
}
.guide-sheet__seat.is-blocked {
  background: #eee;
  color: #aaa;
}
.guide-sheet__aisle {
  width: 14px;
}
/* 명단 페이지 머리줄 - 인쇄할 때만 표시 */
.guide-sheet__print-title {
  display: none;
}

/* 명단 */
.guide-sheet__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.guide-sheet__table th,
.guide-sheet__table td {
  border: 1px solid #ccc;
  padding: 6px 8px;
  text-align: left;
}
.guide-sheet__table th {
  background: #f5f5f5;
}
.guide-sheet__pass {
  margin-top: 1px;
  font-size: 11px;
  color: #555;
  letter-spacing: 0.2px;
}
.guide-sheet__check {
  width: 40px;
  text-align: center !important;
}
.guide-sheet__box {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 1px solid #888;
}

.btn {
  padding: 6px 14px;
  border-radius: 6px;
  border: 1px solid #d0d0d0;
  background: #fff;
  font-size: 13px;
  cursor: pointer;
}
.btn--primary {
  background: #2f7d5f;
  border-color: #2f7d5f;
  color: #fff;
}

@media (max-width: 560px) {
  .guide-sheet__meta {
    grid-template-columns: 1fr;
  }
  .guide-sheet__seat {
    width: 56px;
  }
}

@media print {
  .guide-sheet {
    max-width: none;
    padding: 0;
  }
  .guide-sheet__actions {
    display: none;
  }
  .guide-sheet__bus-map {
    overflow: visible;
  }
  .guide-sheet__seat.is-taken {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .guide-sheet__table tr {
    break-inside: avoid;
  }
  /* 1페이지: 헤더 + 좌석표 (좌석표가 중간에 잘리지 않게) */
  .guide-sheet__section--map {
    break-inside: avoid;
    page-break-inside: avoid;
    margin-bottom: 0;
  }
  /* 2페이지부터: 탑승자 명단 (표 머리글은 페이지마다 반복) */
  .guide-sheet__section--list {
    break-before: page;
    page-break-before: always;
  }
  .guide-sheet__print-title {
    display: block;
    margin: 0 0 8px;
    padding-bottom: 6px;
    border-bottom: 1px solid #222;
    font-size: 13px;
    font-weight: 700;
  }
  .guide-sheet__table thead {
    display: table-header-group;
  }
}
</style>

<style>
/* 인쇄 시 사이트 공통 레이아웃(헤더·푸터 등)은 빼고 좌석표만 */
@media print {
  @page {
    size: A4;
    margin: 12mm;
  }
  body * {
    visibility: hidden;
  }
  .guide-sheet,
  .guide-sheet * {
    visibility: visible;
  }
  .guide-sheet {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
  }
}
</style>